import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import {
  AlertDialogService,
  Destroyable,
  isSavingRental,
  isSomething,
  Rental,
  SaveForm,
  selectAvailableRentalMainUnits,
  selectAvailableRentalSubUnits,
  selectRentalSubUnits,
  setRentalSubUnits,
  setRentalSubUnitsComplete,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { startWith, switchMap } from 'rxjs/operators'
import * as lodash from 'lodash'
import { Observable, of } from 'rxjs'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { RentalDialogTab } from '../../overlays/rental-overlay/rental-overlay.component'
import { RentalGroupSuccessDialogService } from '../rental-group-success-dialog/rental-group-success-dialog.service'
import { Actions, ofType } from '@ngrx/effects'
import { selectLinkedRentals } from '@tv3/store/connection/connection.selectors'

@Component({
  selector: 'app-multi-unit-rental-dialog',
  templateUrl: './multi-unit-rental-dialog.component.html',
  styleUrls: ['./multi-unit-rental-dialog.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class MultiUnitRentalDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    multi_unit_id: ['', [Validators.required]],
    children: [[]],
  })

  get parentIdCtrl() {
    return this.form.get('multi_unit_id') as FormControl
  }

  availableParents$ = this.store.pipe(select(selectAvailableRentalMainUnits))
  availableChildren$: Observable<Rental[]>

  children: Rental[] = []

  loading$ = this.store.pipe(select(isSavingRental))

  linkedRentals: Rental[] = []
  isSubunitSelectable = (rental: Rental) => {
    return !this.linkedRentals.find((r) => r.id === rental.id)
  }

  getTooltip = (rental: Rental) => {
    if (this.linkedRentals.find((r) => r.id === rental.id)) {
      return $localize`This rental is connected to an API channel and cannot be selected as subunit.`
    }
    return null
  }

  constructor(
    public dialogRef: MatDialogRef<MultiUnitRentalDialogComponent>,
    private fb: FormBuilder,
    private store: Store<any>,
    private actions: Actions,
    private successDialog: RentalGroupSuccessDialogService,
    private alert: AlertDialogService,
    @Inject(MAT_DIALOG_DATA) public data: { rentalId?: string }
  ) {
    super()
    if (this.data.rentalId) {
      this.form.patchValue({ multi_unit_id: this.data.rentalId })
    }
  }

  ngOnInit(): void {
    const parentId$ = this.parentIdCtrl.valueChanges.pipe(startWith(this.parentIdCtrl.value))
    parentId$
      .pipe(
        switchMap((id) => this.store.pipe(select(selectRentalSubUnits(id)))),
        untilDestroy(this)
      )
      .subscribe((children) => {
        this.children = children
        this.form.patchValue({ children: children?.map((r) => r.id) })
      })

    this.availableChildren$ = parentId$.pipe(
      switchMap((id) => this.store.pipe(select(selectAvailableRentalSubUnits(id))))
    )

    this.store.pipe(select(selectLinkedRentals), untilDestroy(this)).subscribe((rentals) => {
      this.linkedRentals = rentals
    })

    this.actions
      .pipe(
        ofType(setRentalSubUnitsComplete),
        switchMap(({ updates }) => {
          if (updates.some((t) => t.id === this.data.rentalId && !t.changes.multi_unit_id)) {
            this.showUnlinkedSuccessDialog()
            return of(null)
          }
          return this.showSuccessDialog()
        }),
        untilDestroy(this)
      )
      .subscribe(() => {
        this.close()
      })
  }

  close() {
    this.dialogRef.close()
  }

  showUnlinkedSuccessDialog() {
    return this.alert.open({
      title: $localize`Multi-unit setup unlinked!`,
      body: $localize`The Multi-unit relationship has been successfully removed from selected rentals.`,
    })
  }

  showSuccessDialog() {
    const { multi_unit_id } = this.form.getRawValue()
    return this.successDialog
      .open({
        title: $localize`Multi-Unit Setup Complete!`,
        message: $localize`Your Multi-Unit rental has been successfully created.`,
        image: '/assets/images/rental-group/multi-unit-done-1.svg',
        steps: [
          $localize`You can now add and manage subunits inside the rental details overlay under the "Multi-Unit Settings" tab.`,
          $localize`Reservations will be auto-assigned to subunits based on priority.`,
        ],
        buttons: [
          {
            label: $localize`Multi-Unit Settings`,
            icon: 'cs-icon cs-icon-door-cog',
            classes: 'btn btn-secondary-info',
            action: () => {
              this.store.dispatch(
                OpenRentalOverlay({ rentalId: multi_unit_id, activeTab: RentalDialogTab.MultiUnitSettings })
              )
            },
          },
          {
            label: $localize`Ok, got it`,
            icon: 'cs-icon cs-icon-check text-xs',
            classes: 'btn btn-secondary-info',
          },
        ],
      })
      .afterClosed()
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { children: newChildrenIds, multi_unit_id } = this.form.getRawValue()
    const oldChildrenIds = this.children.map((t) => t.id)

    if (
      !lodash.difference(oldChildrenIds, newChildrenIds).length &&
      !lodash.difference(newChildrenIds, oldChildrenIds).length
    ) {
      this.store.dispatch(setRentalSubUnitsComplete({ updates: [] }))
      this.close()
      return
    }

    const addedChildren = lodash.difference(newChildrenIds, oldChildrenIds)
    const removedChildren = lodash.difference(oldChildrenIds, newChildrenIds)

    // set parent its self
    if (!isSomething(oldChildrenIds) && isSomething(addedChildren)) {
      addedChildren.push(multi_unit_id)
    }

    // clear parent its self
    if (isSomething(oldChildrenIds) && !isSomething(newChildrenIds)) {
      removedChildren.push(multi_unit_id)
    }

    this.store.dispatch(
      setRentalSubUnits({
        rentalId: multi_unit_id,
        addedChildren,
        removedChildren,
      })
    )
  }
}
